import { DropdownItem, Dropdown } from "@/dropdown";
import { AbstractFormControl } from "./form-control";

export interface SelectControlVariables {
    options?: DropdownItem[];
}

export class SelectControl extends AbstractFormControl<HTMLSelectElement> {

    static create(name: string) {
        let select = document.createElement('select');
        select.name = name;
        select.dataset.controller = 'select';
        return select;
    }

    getName(): string {
        return this.element.name;
    }

    getValue() {
        return this.element.value;
    }

    setValue(value: any): void {
        this.element.value = value;
    }

    formatValue() {
        return this.getSelectedOption();
    }

    getSelectedOption() {
        let selected = this.getValue();
        return selected ? this.getOption(selected)?.textContent : null;
    }

    getOption(value: string): HTMLOptionElement | null {
        return this.element.querySelector<HTMLOptionElement>(`option[value='${value}']`);
    }

    addOption(id: string, name: string, alphabetical = true) {
        let newOptionEl = this.newOption(id, name);
        let index = alphabetical ? this.getNextAlphabeticalOption(name) : null;
        this.element.add(newOptionEl, index);
    }

    addNumberedOptions(options: string[]) {
        options.forEach((option, index) => this.addOption((index + 1) + "", option));
    }

    getNumberedItem<T>(items: T[]) {
        return items[parseInt(this.getValue()) - 1];
    }

    private newOption(id: string, name: string, selected = false) {
        let option = document.createElement('option');
        option.value = id;
        option.innerText = name;
        option.selected = selected;
        return option;
    }

    private getNextAlphabeticalOption(name: string) {
        return Dropdown.getNextAlphabeticalItem(name, this.element.options);
    }

    removeOption(id: string) {
        let option = this.getOption(id);
        if (option) {
            option.remove();
        }
    }

    updateOption(id: string, name: string, alphabetical = true) {
        this.removeOption(id);
        this.addOption(id, name, alphabetical);
    }

    setVariables({ options }: SelectControlVariables) {
        if (options) {
            this.replaceOptions(options);
        }
    }

    replaceOptions(options: DropdownItem[]) {
        let prevValue = this.getValue();
        this.element.innerHTML = '';
        for (let option of options) {
            this.element.add(this.newOption(option.id, option.name, option.selected));
        }
        // Restore value if still in option list, and no value preselected
        let preselected = options.some(o => o.selected);
        if (prevValue && !preselected && this.getOption(prevValue)) {
            this.setValue(prevValue);
        }
    }

    setEnabled(enabled: boolean): void {
        this.element.disabled = !enabled;
    }

    matches(text: string): boolean {
        let option = this.getSelectedOption();
        if (option) {
            return text == option;
        }
        return true; // Blank => All
    }
}
